import React, { useState } from 'react';
import { Tabs, TabsContent } from '../ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Building2, Users, BarChart3 } from 'lucide-react';
import { EmployerManagementNew } from './EmployerManagementNew';
import { CompanyManagementNew } from './CompanyManagementNew';
import { ReportManagement } from './ReportManagement';
import { apiClient } from '../../lib/api-client';

interface EmployerCompanyManagementProps {
  companies: any[];
  jobs: any[];
  applications: any[];
  onCompanyUpdate: (companies: any[]) => void;
}

export function EmployerCompanyManagement({
  companies,
  jobs,
  applications,
  onCompanyUpdate,
}: EmployerCompanyManagementProps) {
  const safeCompanies = Array.isArray(companies) ? companies : [];
  const safeJobs = Array.isArray(jobs) ? jobs : [];
  const safeApplications = Array.isArray(applications) ? applications : [];

  const [activeTab, setActiveTab] = useState<string>('overview');

  const totalCompanies = safeCompanies.length;
  const verified = safeCompanies.filter((c) => !!c?.verified).length;
  const unverified = totalCompanies - verified;

  const onCompanyUpdateServerDriven = async (nextCompanies: any[]) => {
    try { onCompanyUpdate(nextCompanies); } catch { }
    try {
      const refreshed = await apiClient.getAdminCompanies({ page: 1, limit: 2000 });
      onCompanyUpdate(refreshed || []);
    } catch (err) {
      console.warn('Company refresh failed:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Companies</p>
              <p className="text-2xl font-bold text-blue-600">{totalCompanies}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Verified</p>
              <p className="text-2xl font-bold text-green-600">{verified}</p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending/Unverified</p>
              <p className="text-2xl font-bold text-yellow-600">{unverified}</p>
            </div>
            <BarChart3 className="h-8 w-8 text-yellow-600" />
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-center mb-8">
          <Menubar className="h-12 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full p-1 shadow-lg shadow-gray-200/20 relative w-full">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'profiles', label: 'Company Profiles', icon: Building2 },
              { id: 'reports', label: 'Reports', icon: Users }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <MenubarMenu key={tab.id} className="flex-1">
                  <MenubarTrigger
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`
                      relative z-10 flex cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 w-full
                      ${isActive ? 'text-white font-semibold' : 'text-gray-500 hover:text-gray-900'}
                      bg-transparent border-none shadow-none focus:bg-transparent data-[state=open]:bg-transparent
                    `}
                  >
                    <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeEmployerCompanyTabIndicator"
                        className="absolute inset-0 bg-[#e37300] rounded-full shadow-sm"
                        style={{ zIndex: -1 }}
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </MenubarTrigger>
                </MenubarMenu>
              );
            })}
          </Menubar>
        </div>

        <TabsContent value="overview">
          <EmployerManagementNew
            companies={safeCompanies}
            jobs={safeJobs}
            applications={safeApplications}
            onCompanyUpdate={onCompanyUpdateServerDriven}
          />
        </TabsContent>

        <TabsContent value="profiles">
          <CompanyManagementNew
            companies={safeCompanies}
            onCompanyUpdate={onCompanyUpdateServerDriven}
          />
        </TabsContent>

        <TabsContent value="reports">
          <ReportManagement
            jobs={safeJobs}
            users={[]}
            applications={safeApplications}
            companies={safeCompanies}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button onClick={() => setActiveTab('profiles')} className="bg-primary hover:bg-primary/90">
          Add Company
        </Button>
      </div>
    </div>
  );
}
